import { Field, Select } from '../components';

/**
 * Everything an author can set on a question, in one place.
 *
 * Shared by the form editor and the question library. They were separate, and the library
 * ended up with a create form that took a prompt and a type and nothing else — so a
 * Photograph question added to the library could not say which camera, and the setting
 * silently defaulted on every copy made from it. One editor, used twice.
 */

export interface QuestionNode {
  id: string;
  fieldKey: string;
  prompt: string;
  helpText: string | null;
  stepType: string;
  displayOrder: number;
  isRequired: boolean;
  requiresEvidence: boolean;
  cameraFacing: string;
  dateMode: string;
  minDateMode: string;
  maxDateMode: string;
  minDate: string | null;
  maxDate: string | null;
  minValue: string | null;
  maxValue: string | null;
  parentStepId: string | null;
  parentOptionId: string | null;
  options: Array<{
    id: string;
    value: string;
    label: string;
    displayOrder: number;
    /** Choosing this answer puts the visit on the escalations list. It never blocks. */
    escalates: boolean;
    escalationNote: string | null;
    children: QuestionNode[];
  }>;
  children: QuestionNode[];
}

/** Everything an author can set on a question, in one shape both editors share. */
export interface QuestionDraft {
  prompt: string;
  helpText: string;
  isRequired: boolean;
  requiresEvidence: boolean;
  cameraFacing: string;
  dateMode: string;
  minDateMode: string;
  maxDateMode: string;
  minDate: string;
  maxDate: string;
  minValue: string;
  maxValue: string;
}

/**
 * The fields these helpers actually read.
 *
 * Narrower than `QuestionNode` on purpose: a library entry carries the same *settings* but
 * none of a form step's identity — no field key, no parent, no display order. Asking for the
 * whole node would have forced the library to invent values it has no meaning for.
 */
export interface QuestionSettingsSource {
  stepType: string;
  prompt: string;
  helpText: string | null;
  isRequired: boolean;
  requiresEvidence: boolean;
  cameraFacing: string;
  dateMode: string;
  minDateMode: string;
  maxDateMode: string;
  minDate: string | null;
  maxDate: string | null;
  minValue: string | null;
  maxValue: string | null;
}

export function draftOf(question: QuestionSettingsSource): QuestionDraft {
  return {
    prompt: question.prompt,
    helpText: question.helpText ?? '',
    isRequired: question.isRequired,
    requiresEvidence: question.requiresEvidence,
    cameraFacing: question.cameraFacing,
    dateMode: question.dateMode,
    minDateMode: question.minDateMode,
    maxDateMode: question.maxDateMode,
    minDate: question.minDate ?? '',
    maxDate: question.maxDate ?? '',
    minValue: question.minValue ?? '',
    maxValue: question.maxValue ?? '',
  };
}

export const EMPTY_DRAFT: QuestionDraft = {
  prompt: '',
  helpText: '',
  isRequired: true,
  requiresEvidence: false,
  cameraFacing: 'ANY',
  dateMode: 'DATE',
  minDateMode: 'NONE',
  maxDateMode: 'NONE',
  minDate: '',
  maxDate: '',
  minValue: '',
  maxValue: '',
};

const CAMERA_FACINGS = [
  { value: 'ANY', label: 'Either — the field user chooses' },
  { value: 'BACK', label: 'Back camera only' },
  { value: 'FRONT', label: 'Front camera only' },
];

const DATE_MODES = [
  { value: 'DATE', label: 'A date' },
  { value: 'TIME', label: 'A time of day' },
  { value: 'DATETIME', label: 'A date and a time' },
];

/** The HTML input type, and the format the bound is stored in, for each date mode. */
const DATE_INPUT: Record<string, { type: 'date' | 'time' | 'datetime-local'; hint: string }> = {
  DATE: { type: 'date', hint: 'YYYY-MM-DD' },
  TIME: { type: 'time', hint: 'HH:MM' },
  DATETIME: { type: 'datetime-local', hint: 'YYYY-MM-DDTHH:MM' },
};

/**
 * How each bound is decided.
 *
 * `TODAY` is the one that matters. "No past dates" and "no future dates" are the two most
 * common rules an author wants, and a fixed date cannot express either — it would have to be
 * re-edited every morning to keep meaning the same thing, and nobody would.
 */
const MIN_BOUND_MODES = [
  { value: 'NONE', label: 'No earliest limit' },
  { value: 'TODAY', label: 'Today — no dates in the past' },
  { value: 'FIXED', label: 'A fixed date' },
];

const MAX_BOUND_MODES = [
  { value: 'NONE', label: 'No latest limit' },
  { value: 'TODAY', label: 'Today — no dates in the future' },
  { value: 'FIXED', label: 'A fixed date' },
];

/**
 * The settings a question actually has, decided by its type.
 *
 * Shown per type rather than all at once. An author setting up a photograph has no use for a
 * minimum date, and a form that offers every field for every type is how you end up with
 * stored values nothing reads.
 */
export function QuestionSettings({
  stepType,
  draft,
  onChange,
  promptLabel,
}: {
  stepType: string;
  draft: QuestionDraft;
  onChange: (next: QuestionDraft) => void;
  promptLabel: string;
}): JSX.Element {
  const set = <K extends keyof QuestionDraft>(key: K, value: QuestionDraft[K]): void =>
    onChange({ ...draft, [key]: value });

  const dateInput = DATE_INPUT[draft.dateMode] ?? DATE_INPUT['DATE'];

  return (
    <>
      <div className="form-row">
        <Field
          label={promptLabel}
          value={draft.prompt}
          onChange={(value) => set('prompt', value)}
          required
        />
      </div>

      <div className="form-row">
        <Field
          label="Help text"
          value={draft.helpText}
          onChange={(value) => set('helpText', value)}
          hint="Shown under the question on the phone. Optional."
        />
      </div>

      <div className="row">
        <label className="row">
          <input
            type="checkbox"
            checked={draft.isRequired}
            onChange={() => set('isRequired', !draft.isRequired)}
          />
          <span>Must be answered</span>
        </label>
        {/* A Photograph question is a request for a photograph; the flag would be noise. */}
        {stepType !== 'PHOTO' && stepType !== 'DOCUMENT' && stepType !== 'SIGNATURE' && (
          <label className="row">
            <input
              type="checkbox"
              checked={draft.requiresEvidence}
              onChange={() => set('requiresEvidence', !draft.requiresEvidence)}
            />
            <span>Needs a photograph or document</span>
          </label>
        )}
      </div>

      {stepType === 'PHOTO' && (
        <div className="form-row">
          <Select
            label="Which camera"
            value={draft.cameraFacing}
            onChange={(value) => set('cameraFacing', value)}
            options={CAMERA_FACINGS}
          />
          <p className="muted">
            Front for anything meant to show who is present — a rear-camera photograph
            proves nothing about who was holding the phone. There is no gallery picker on any
            setting.
          </p>
        </div>
      )}

      {stepType === 'DATE' && (
        <>
          <div className="form-row">
            <Select
              label="Ask for"
              value={draft.dateMode}
              onChange={(value) => set('dateMode', value)}
              options={DATE_MODES}
            />
          </div>
          <div className="form-row">
            <Select
              label="Earliest allowed"
              value={draft.minDateMode}
              onChange={(value) => set('minDateMode', value)}
              options={MIN_BOUND_MODES}
            />
            {draft.minDateMode === 'FIXED' && (
              <Field
                label="Not before"
                type={dateInput?.type}
                value={draft.minDate}
                onChange={(value) => set('minDate', value)}
                hint={dateInput?.hint ?? ''}
              />
            )}
          </div>

          <div className="form-row">
            <Select
              label="Latest allowed"
              value={draft.maxDateMode}
              onChange={(value) => set('maxDateMode', value)}
              options={MAX_BOUND_MODES}
            />
            {draft.maxDateMode === 'FIXED' && (
              <Field
                label="Not after"
                type={dateInput?.type}
                value={draft.maxDate}
                onChange={(value) => set('maxDate', value)}
                hint={dateInput?.hint ?? ''}
              />
            )}
          </div>
        </>
      )}

      {stepType === 'NUMBER' && (
        <div className="form-row">
          <Field
            label="Minimum"
            type="number"
            value={draft.minValue}
            onChange={(value) => set('minValue', value)}
            hint="Optional."
          />
          <Field
            label="Maximum"
            type="number"
            value={draft.maxValue}
            onChange={(value) => set('maxValue', value)}
            hint="Optional."
          />
        </div>
      )}
    </>
  );
}

/** The per-type settings, read back as a sentence rather than left invisible. */
export function QuestionSummary({
  question,
}: {
  question: QuestionSettingsSource;
}): JSX.Element | null {
  const parts: string[] = [];

  if (question.stepType === 'PHOTO' && question.cameraFacing !== 'ANY') {
    parts.push(question.cameraFacing === 'FRONT' ? 'Front camera only' : 'Back camera only');
  }

  if (question.stepType === 'DATE') {
    parts.push(DATE_MODES.find((mode) => mode.value === question.dateMode)?.label ?? '');

    if (question.minDateMode === 'TODAY') parts.push('no dates in the past');
    else if (question.minDateMode === 'FIXED' && question.minDate !== null) {
      parts.push(`from ${question.minDate}`);
    }

    if (question.maxDateMode === 'TODAY') parts.push('no dates in the future');
    else if (question.maxDateMode === 'FIXED' && question.maxDate !== null) {
      parts.push(`to ${question.maxDate}`);
    }
  }

  if (question.stepType === 'NUMBER') {
    if (question.minValue !== null) parts.push(`min ${question.minValue}`);
    if (question.maxValue !== null) parts.push(`max ${question.maxValue}`);
  }

  if (parts.length === 0) return null;
  return <p className="muted">{parts.filter((part) => part !== '').join(' · ')}</p>;
}

